import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { SuccessData } from '../types/analytics.types';
import './SuccessRatesChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface SuccessRatesChartProps {
  data: SuccessData;
  loading?: boolean;
}

type ViewMode = 'component' | 'difficulty';

export const SuccessRatesChart: React.FC<SuccessRatesChartProps> = ({ data, loading }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('component');

  if (loading) {
    return (
      <div className="success-chart">
        <div className="chart-header">
          <h4>Success Rates</h4>
        </div>
        <div className="loading-skeleton" style={{ height: '300px' }}></div>
      </div>
    );
  }

  if (!data) {
    return <div className="no-data">No success data available</div>;
  }

  const displayData = viewMode === 'component' ? data.by_component : data.by_difficulty;

  // Sort by total attempts
  const sortedData = [...displayData].sort((a, b) => b.total - a.total);

  const labels = sortedData.map(item =>
    viewMode === 'component'
      ? (item.component || 'Unknown')
      : `Level ${item.difficulty || 'N/A'}`
  );

  const successRates = sortedData.map(item => item.success_rate);
  const correctCounts = sortedData.map(item => item.correct);
  const incorrectCounts = sortedData.map(item => item.incorrect);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Success Rate (%)',
        data: successRates,
        backgroundColor: successRates.map(rate => {
          if (rate >= 80) return 'rgba(75, 192, 192, 0.8)';
          if (rate >= 60) return 'rgba(255, 206, 86, 0.8)';
          return 'rgba(255, 99, 132, 0.8)';
        }),
        borderColor: successRates.map(rate => {
          if (rate >= 80) return 'rgba(75, 192, 192, 1)';
          if (rate >= 60) return 'rgba(255, 206, 86, 1)';
          return 'rgba(255, 99, 132, 1)';
        }),
        borderWidth: 2
      }
    ]
  };

  const chartOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: function(context: any) {
            const index = context.dataIndex;
            const item = sortedData[index];
            return [
              `Success Rate: ${item.success_rate.toFixed(1)}%`,
              `Correct: ${item.correct.toLocaleString()}`,
              `Incorrect: ${item.incorrect.toLocaleString()}`,
              `Total Attempts: ${item.total.toLocaleString()}`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          callback: function(value: any) {
            return value + '%';
          },
          font: {
            size: 11
          }
        }
      },
      y: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    }
  };

  return (
    <div className="success-chart">
      <div className="chart-header">
        <h4>Success Rates Analysis</h4>
        <div className="view-mode-selector">
          <button
            className={viewMode === 'component' ? 'active' : ''}
            onClick={() => setViewMode('component')}
          >
            By Component
          </button>
          <button
            className={viewMode === 'difficulty' ? 'active' : ''}
            onClick={() => setViewMode('difficulty')}
          >
            By Difficulty
          </button>
        </div>
      </div>
      <div className="chart-wrapper" style={{ height: '350px' }}>
        <Bar data={chartData} options={chartOptions} />
      </div>
      <div className="chart-summary">
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Overall Success Rate:</span>
            <span className="stat-value">{data.overall_success_rate.toFixed(1)}%</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Scored Actions:</span>
            <span className="stat-value">{data.total_scored_actions.toLocaleString()}</span>
          </div>
          {data.avg_response_time_ms > 0 && (
            <div className="stat-item">
              <span className="stat-label">Avg Response Time:</span>
              <span className="stat-value">{(data.avg_response_time_ms / 1000).toFixed(1)}s</span>
            </div>
          )}
        </div>
        <div className="legend-hint">
          <span className="legend-item">
            <span className="dot green"></span> Excellent (≥80%)
          </span>
          <span className="legend-item">
            <span className="dot yellow"></span> Good (60-79%)
          </span>
          <span className="legend-item">
            <span className="dot red"></span> Needs Improvement (&lt;60%)
          </span>
        </div>
      </div>
    </div>
  );
};

export default SuccessRatesChart;
